import { useTranslation } from "react-i18next";
import { DoubleMaterialityESRSAreaEnum } from "@green0meter/api-client";

import type { DoubleMaterialityESRSArea } from "./ESRS.constants";
import { ESRS_ENUM_TO_STRING, ESRS_STRING_TO_ENUM } from "./ESRS.constants";

type EsrsLabelMap = Record<DoubleMaterialityESRSArea, string>;

type EsrsOption = {
  code: DoubleMaterialityESRSArea;
  enumValue: DoubleMaterialityESRSAreaEnum;
  label: string;
};

export const useESRSLabels = (): {
  ESRS_LABELS: EsrsLabelMap;
  ESRS_OPTIONS: EsrsOption[];
  getEsrsLabel: (code: DoubleMaterialityESRSArea | null | undefined) => string;
  getEsrsLabelFromEnum: (
    enumValue: DoubleMaterialityESRSAreaEnum | null | undefined,
  ) => string;
} => {
  const { t } = useTranslation();

  const ESRS_LABELS: EsrsLabelMap = {
    E1: t("dma.esrsLabels.e1", "E1: Climate Change"),
    E2: t("dma.esrsLabels.e2", "E2: Pollution"),
    E3: t("dma.esrsLabels.e3", "E3: Water and Marine Resources"),
    E4: t("dma.esrsLabels.e4", "E4: Biodiversity and Ecosystems"),
    E5: t("dma.esrsLabels.e5", "E5: Circular Economy"),
    S1: t("dma.esrsLabels.s1", "S1: Own Workforce"),
    S2: t("dma.esrsLabels.s2", "S2: Workers in Value Chain"),
    S3: t("dma.esrsLabels.s3", "S3: Affected Communities"),
    S4: t("dma.esrsLabels.s4", "S4: Consumers and End Users"),
    G1: t("dma.esrsLabels.g1", "G1: Business Conduct"),
    OTHER: t("dma.esrsLabels.other", "Other"),
  };

  const getEsrsLabel = (
    code: DoubleMaterialityESRSArea | null | undefined,
  ): string => {
    if (!code) return "";

    return ESRS_LABELS[code] ?? String(code);
  };

  const getEsrsLabelFromEnum = (
    enumValue: DoubleMaterialityESRSAreaEnum | null | undefined,
  ): string => {
    if (enumValue === null || enumValue === undefined) return "";

    const code = ESRS_ENUM_TO_STRING[enumValue];

    return code ? getEsrsLabel(code) : `ESRS ${enumValue}`;
  };

  const ESRS_OPTIONS: EsrsOption[] = (
    Object.entries(ESRS_LABELS) as Array<[DoubleMaterialityESRSArea, string]>
  ).map(([code, label]) => ({
    code,
    enumValue: ESRS_STRING_TO_ENUM[code],
    label,
  }));

  return {
    ESRS_LABELS,
    ESRS_OPTIONS,
    getEsrsLabel,
    getEsrsLabelFromEnum,
  };
};
